import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useStore } from 'zustand';
import type { AudienceTargetCountStatus } from '@/components/audiences/audience-target-count';
import { mapFilterDtoToTree } from '@/components/audiences/map-filter-dto-to-tree';
import { mapTreeToFilterNodeDto } from '@/components/audiences/map-tree-to-filter-dto';
import { toast } from '@/components/ui/sonner';
import {
  useAudience,
  usePreviewAudience,
  useUpdateAudience,
} from '@/hooks/use-audiences';
import { useI18nContext } from '@/i18n/i18n-react';
import type { Locales } from '@/i18n/i18n-types';
import { treeDndStore } from '@/components/tree-dnd/tree-dnd-store';
import {
  treeHasConditions,
  treeToSerializable,
} from '@/components/tree-dnd/tree-dnd-utils';

const PREVIEW_DEBOUNCE_MS = 500;

export function useEditAudiencePage() {
  const tenantSlug = useParams().tenantSlug ?? '';
  const audienceId = useParams().audienceId ?? '';
  const navigate = useNavigate();
  const { LL, locale } = useI18nContext();
  const tree = useStore(treeDndStore, (state) => state.tree);
  const audienceQuery = useAudience(audienceId);
  const { mutateAsync: previewAudience } = usePreviewAudience();
  const { mutateAsync: updateAudience } = useUpdateAudience();

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [targetUsersCount, setTargetUsersCount] = useState<
    number | undefined
  >();
  const [totalUserbaseCount, setTotalUserbaseCount] = useState(0);
  const [lastCalculatedTreeKey, setLastCalculatedTreeKey] = useState<
    string | null
  >(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const previewTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const previewRequestIdRef = useRef(0);
  const initializedAudienceIdRef = useRef<string | null>(null);

  const treeKey = useMemo(
    () => JSON.stringify(treeToSerializable(tree)),
    [tree]
  );
  const hasFilter = treeHasConditions(tree);

  const targetCountStatus = useMemo((): AudienceTargetCountStatus => {
    if (!hasFilter) return 'empty';
    if (isPreviewLoading) return 'loading';
    if (lastCalculatedTreeKey === treeKey) return 'fresh';
    return 'stale';
  }, [hasFilter, isPreviewLoading, lastCalculatedTreeKey, treeKey]);

  const isSaveDisabled = !hasFilter || isSaving;

  const validateName = useCallback(
    (nameStr: string) => {
      if (!nameStr.trim()) {
        return LL.audiences.create.validation.nameRequired();
      }
      return '';
    },
    [LL]
  );

  useEffect(() => {
    const audience = audienceQuery.data;
    if (!audience || initializedAudienceIdRef.current === audience.id) return;

    if (audience.type !== 'filter' || !audience.filters) {
      navigate(`/${tenantSlug}/audiences/${audienceId}`);
      return;
    }

    treeDndStore.getState().setTree(mapFilterDtoToTree(audience.filters));
    setName(audience.name);
    initializedAudienceIdRef.current = audience.id;
  }, [audienceId, audienceQuery.data, navigate, tenantSlug]);

  useEffect(() => {
    return () => {
      initializedAudienceIdRef.current = null;
      previewRequestIdRef.current += 1;
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
      }
      treeDndStore.getState().reset();
    };
  }, []);

  const runPreview = useCallback(
    async (forTreeKey: string) => {
      const requestId = ++previewRequestIdRef.current;
      setIsPreviewLoading(true);

      try {
        const filters = mapTreeToFilterNodeDto(treeDndStore.getState().tree);
        const result = await previewAudience(filters);
        if (requestId !== previewRequestIdRef.current) return;

        setTargetUsersCount(result.targetedUserCount);
        setTotalUserbaseCount(result.notificationsEnabledUserCount);
        setLastCalculatedTreeKey(forTreeKey);
      } catch (error) {
        if (requestId !== previewRequestIdRef.current) return;

        setTargetUsersCount(undefined);
        setTotalUserbaseCount(0);
        setLastCalculatedTreeKey(null);

        toast.error(LL.general.error(), {
          description:
            error instanceof Error
              ? error.message
              : LL.audiences.edit.toast.failedToUpdateAudience(),
          duration: 3000,
        });
      } finally {
        if (requestId === previewRequestIdRef.current) {
          setIsPreviewLoading(false);
        }
      }
    },
    [LL, previewAudience]
  );

  const handleRecalculateTargetCount = useCallback(() => {
    if (!hasFilter || isPreviewLoading) return;

    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
      previewTimeoutRef.current = null;
    }

    void runPreview(treeKey);
  }, [hasFilter, isPreviewLoading, runPreview, treeKey]);

  useEffect(() => {
    if (!hasFilter) {
      previewRequestIdRef.current += 1;
      setTargetUsersCount(undefined);
      setTotalUserbaseCount(0);
      setLastCalculatedTreeKey(null);
      setIsPreviewLoading(false);
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
        previewTimeoutRef.current = null;
      }
      return;
    }

    if (previewTimeoutRef.current) {
      clearTimeout(previewTimeoutRef.current);
    }

    setIsPreviewLoading(true);
    previewTimeoutRef.current = setTimeout(() => {
      previewTimeoutRef.current = null;
      void runPreview(treeKey);
    }, PREVIEW_DEBOUNCE_MS);

    return () => {
      if (previewTimeoutRef.current) {
        clearTimeout(previewTimeoutRef.current);
        previewTimeoutRef.current = null;
      }
    };
  }, [hasFilter, runPreview, treeKey]);

  const handleNameChange = useCallback(
    (value: string) => {
      setName(value);
      setNameError(validateName(value));
    },
    [validateName]
  );

  const handleSave = useCallback(async () => {
    if (!hasFilter || isSaving) return;

    const trimmedName = name.trim();
    const error = validateName(trimmedName);
    setNameError(error);
    if (error) return;

    setIsSaving(true);
    try {
      const filters = mapTreeToFilterNodeDto(tree);
      await updateAudience({
        audienceId,
        body: { name: trimmedName, filters },
      });

      toast.success(LL.audiences.edit.toast.audienceUpdatedTitle(), {
        description: LL.audiences.edit.toast.audienceUpdatedDescription({
          audienceName: trimmedName,
        }),
        duration: 3000,
      });
      navigate(`/${tenantSlug}/audiences/${audienceId}`);
    } catch (error) {
      setIsSaving(false);
      toast.error(LL.general.error(), {
        description:
          error instanceof Error
            ? error.message
            : LL.audiences.edit.toast.failedToUpdateAudience(),
        duration: 3000,
      });
    }
  }, [
    LL,
    audienceId,
    hasFilter,
    isSaving,
    name,
    navigate,
    tenantSlug,
    tree,
    updateAudience,
    validateName,
  ]);

  const handleCancel = useCallback(() => {
    navigate(`/${tenantSlug}/audiences/${audienceId}`);
  }, [audienceId, navigate, tenantSlug]);

  return {
    tenantSlug,
    audienceId,
    locale: locale as Locales,
    name,
    nameError,
    onNameChange: handleNameChange,
    targetCountStatus,
    targetUsersCount,
    totalUserbaseCount,
    onRecalculateTargetCount: handleRecalculateTargetCount,
    onCancel: handleCancel,
    onCreate: handleSave,
    isCreating: isSaving,
    isCreateDisabled: isSaveDisabled,
    isSaveNameDialogOpen: false,
    onSaveNameDialogOpenChange: () => {},
    saveNameDialogName: '',
    onSaveNameConfirm: () => {},
    isLoadingAudience: audienceQuery.isLoading,
    isAudienceError:
      audienceQuery.isError ||
      (audienceQuery.data &&
        (audienceQuery.data.type !== 'filter' || !audienceQuery.data.filters)),
  };
}
